/**
 * Componente: ActivityTimeline
 * Timeline de atividades do médico durante o dia
 */

import React from 'react';
import { CheckCircle2, Clock, AlertCircle } from 'lucide-react';

interface TimelineActivity {
  id: string;
  type: 'appointment' | 'checkin' | 'note' | 'document' | 'alert';
  title: string;
  time: string;
  description?: string;
  status?: 'completed' | 'pending' | 'warning';
  patientName?: string;
  appointmentType?: 'telemedicine' | 'home_visit';
}

interface ActivityTimelineProps {
  activities?: TimelineActivity[];
  isLoading?: boolean;
}

const typeConfig = {
  appointment: {
    icon: CheckCircle2,
    color: 'text-teal-600 dark:text-teal-400',
    bgColor: 'bg-teal-50 dark:bg-teal-900/20',
  },
  checkin: {
    icon: Clock,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
  },
  note: {
    icon: AlertCircle,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
  },
  document: {
    icon: AlertCircle,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
  },
  alert: {
    icon: AlertCircle,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
  },
};

const mockActivities: TimelineActivity[] = [
  {
    id: '1',
    type: 'appointment',
    title: 'Check-in Pós Operatório',
    time: '10:20 - 10:35',
    patientName: 'Arthur Morgan',
    status: 'completed',
    appointmentType: 'telemedicine',
  },
  {
    id: '2',
    type: 'checkin',
    title: 'Início Consulta',
    time: '09:15',
    patientName: 'Sarah Miller',
    status: 'completed',
  },
  {
    id: '3',
    type: 'note',
    title: 'Agendamento Observado',
    time: '08:45',
    patientName: 'James Wilson',
    description: 'Visita Domiciliar',
    status: 'pending',
  },
  {
    id: '4',
    type: 'document',
    title: 'Documento Enviado',
    time: '08:30',
    patientName: 'Emily Brown',
    description: 'Receita Medica',
    status: 'completed',
  },
  {
    id: '5',
    type: 'appointment',
    title: 'Retorno',
    time: '08:00',
    patientName: 'Michael Scott',
    status: 'completed',
  },
];

export function ActivityTimeline({
  activities = mockActivities,
  isLoading = false,
}: ActivityTimelineProps) {
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-900 rounded-lg p-6 border border-gray-200 dark:border-neutral-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Timeline Hoje
        </h3>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2" />
          <p className="text-sm text-gray-600 dark:text-gray-400">Carregando atividades...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-lg p-6 border border-gray-200 dark:border-neutral-800">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
        Timeline Hoje
      </h3>

      {activities.length === 0 ? (
        <div className="text-center py-12">
          <Clock className="w-12 h-12 text-gray-300 dark:text-neutral-700 mx-auto mb-3" />
          <p className="text-sm text-gray-600 dark:text-gray-400">Nenhuma atividade registrada</p>
        </div>
      ) : (
        <div className="space-y-4">
          {activities.map((activity, index) => {
            const config = typeConfig[activity.type];
            const Icon = config.icon;

            return (
              <div key={activity.id} className="flex gap-4">
                {/* Timeline dot and line */}
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full ${config.bgColor} flex items-center justify-center shrink-0`}>
                    <Icon className={`w-6 h-6 ${config.color}`} />
                  </div>
                  {index < activities.length - 1 && (
                    <div className="w-0.5 h-8 bg-gray-200 dark:bg-neutral-700 mt-2" />
                  )}
                </div>

                {/* Activity content */}
                <div className="flex-1 pt-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                        {activity.title}
                      </h4>
                      {activity.patientName && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {activity.patientName}
                          {activity.description && ` • ${activity.description}`}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-500 font-medium whitespace-nowrap ml-2">
                      {activity.time}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
